import { IonDatetime, IonIcon, IonModal, isPlatform, type DatetimeCustomEvent } from "@ionic/react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs, { type Dayjs } from "dayjs";
import { refreshCircleOutline } from "ionicons/icons";
import React, { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ARRIVAL_ICON, ARRIVAL_LABEL, DEPARTURE_LABEL } from "../../config/CalendarIcons";
import SearchBarDepartureIcon from "../../image/icons/searchbar/SearchBarDepartureIcon.svg?react";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { formatDateForInput } from "../../tools/TimeRelated";
import { type DateWithMode } from "../../types/Forms";
import {
    createCalendarModalEnterAnimation,
    createCalendarModalLeaveAnimation,
} from "./calendarModal.animation";
import "./style.css";

type DateMode = "departure" | "arrival";

interface ModalProps {
    "isOpen":           boolean;
    "onClose":          () => void;
    "date":             DateWithMode;
    "setDate":          Dispatch<SetStateAction<DateWithMode>>;
    "setIsEditedDate"?: Dispatch<SetStateAction<boolean>>;
    "isDarkUi"?:        boolean;
}

const MuiCalendar: React.FC<ModalProps> = ( props: ModalProps ) => {
    const { isOpen, onClose, setDate, setIsEditedDate, isDarkUi = false } = props;
    const modal = useRef<HTMLIonModalElement>( null );
    const isIOS = isPlatform( "ios" );
    const palette = getUIColorPalette( isDarkUi );
    const previousDateRef = useRef<DateWithMode>( props.date );

    const [ selectedDate, setSelectedDate ] = useState<string>( () => formatDateForInput( props.date.timestamp ) );
    const [ selectedTime, setSelelectTime ] = useState<Dayjs | null>( () => dayjs( props.date.timestamp ) );
    const [ iosDateTimeValue, setIosDateTimeValue ] = useState<string>( () => formatDateForInput( props.date.timestamp ) );
    const [ dateMode, setDateMode ] = useState<DateMode>( props.date.isDepartureTimestamp ? "departure" : "arrival" );

    const resetState = useCallback( () => {
        const formattedDate = formatDateForInput( props.date.timestamp );
        setSelectedDate( formattedDate );
        setSelelectTime( dayjs( props.date.timestamp ) );
        if ( isIOS ) {
            setIosDateTimeValue( formattedDate );
        }
        setDateMode( props.date.isDepartureTimestamp ? "departure" : "arrival" );
        previousDateRef.current = props.date;
    }, [ props.date, isIOS ] );

    const handleDateChange = useCallback( ( e: DatetimeCustomEvent ) => {
        if ( typeof e.detail.value === "string" ) {
            setSelectedDate( e.detail.value );

            // On iOS, when presentation is "date-time", the value includes both date and time
            if ( isIOS ) {
                setIosDateTimeValue( e.detail.value );
            }
        }
    }, [ isIOS ] );

    const handleConfirm = useCallback( () => {
        let confirmedDate: Date;

        if ( isIOS ) {
            // On iOS, use the date-time value from IonDatetime directly
            confirmedDate = new Date( iosDateTimeValue );
        } else {
            // On other platforms, combine date from IonDatetime and time from TimePicker
            if ( selectedTime === null ) {
                return;
            }
            confirmedDate = new Date( selectedDate );
            confirmedDate.setHours( selectedTime.hour() );
            confirmedDate.setMinutes( selectedTime.minute() );
            confirmedDate.setSeconds( selectedTime.second() );
        }

        setDate( {
            "timestamp":            confirmedDate.getTime(),
            "isDepartureTimestamp": dateMode === "departure",
        } );
        if ( setIsEditedDate != null ) {
            setIsEditedDate( true );
        }
        onClose();
    }, [ isIOS, iosDateTimeValue, selectedTime, selectedDate, dateMode, setDate, setIsEditedDate, onClose ] );

    const handleDidDismiss = useCallback( () => {
        onClose();
    }, [ onClose ] );

    const handleDidPresent = useCallback( () => {
        resetState();
    }, [ resetState ] );

    const handleTimePickerChange = useCallback( ( newTime: Dayjs | null ) => {
        setSelelectTime( newTime );
    }, [] );

    const handleRefreshClick = useCallback( () => {
        const now = Date.now();
        const formattedNow = formatDateForInput( now );
        setSelectedDate( formattedNow );
        setSelelectTime( dayjs( now ) );
        if ( isIOS ) {
            setIosDateTimeValue( formattedNow );
        }
    }, [ isIOS ] );

    const handleConfirmClick = useCallback( () => {
        void modal.current?.dismiss();
        handleConfirm();
    }, [ handleConfirm ] );

    const handleTimeInputKeyDown = useCallback( ( event: React.KeyboardEvent<HTMLInputElement> ) => {
        if ( event.key === "Enter" ) {
            event.preventDefault();
            handleConfirmClick();
        }
    }, [ handleConfirmClick ] );

    const handleModeChange = useCallback( ( _event: React.MouseEvent<HTMLElement>, newMode: DateMode | null ) => {
        if ( newMode !== null && ( newMode === "departure" || newMode === "arrival" ) ) {
            setDateMode( newMode );
        }
    }, [] );

    const minSelectableDate = useMemo( () => {
        const minDate = new Date();
        minDate.setHours( 0, 0, 0, 0 );
        minDate.setDate( minDate.getDate() - 2 );

        return formatDateForInput( minDate.getTime() );
    }, [] );

    const calendarThemeStyle = useMemo( () => {
        const calendarBackgroundRgb = isDarkUi ? "22, 47, 86" : "255, 255, 255";

        return {
            "backgroundColor":                  palette.modalCardBackground,
            "color":                            palette.modalCardTextPrimary,
            "--calendar-bg":                    palette.modalCardBackground,
            "--calendar-bg-rgb":                calendarBackgroundRgb,
            "--calendar-text":                  palette.modalCardTextPrimary,
            "--calendar-header-text":           palette.overlayTextPrimary,
            "--calendar-weekday-text":          palette.overlayTextSecondary,
            "--calendar-today":                 palette.brandPrimary,
            "--calendar-focus-bg":              "rgba(17, 211, 156, 0.2)",
            "--calendar-focus-shadow":          "rgba(17, 211, 156, 0.2)",
            "--calendar-active-bg":             palette.brandPrimary,
            "--calendar-active-border":         palette.brandPrimary,
            "--calendar-active-text":           palette.textOnBrandPrimary,
            "--calendar-ios-active-bg":         "rgba(17, 211, 156, 0.2)",
            "--calendar-ios-active-text":       palette.brandPrimary,
            "--calendar-picker-bg":             palette.modalCardBackground,
            "--calendar-picker-bg-rgb":         calendarBackgroundRgb,
            "--calendar-wheel-highlight-bg":    "rgba(17, 211, 156, 0.3)",
            "--calendar-wheel-highlight-color": palette.textOnBrandPrimary,
        } as React.CSSProperties;
    }, [ isDarkUi, palette ] );

    return (
        <IonModal
            enterAnimation={createCalendarModalEnterAnimation}
            focusTrap={false}
            id="style1-modal"
            isOpen={isOpen}
            leaveAnimation={createCalendarModalLeaveAnimation}
            onDidDismiss={handleDidDismiss}
            onDidPresent={handleDidPresent}
            ref={modal}
        >
            <div
                className="p-3"
                style={calendarThemeStyle}
            >
                <ToggleButtonGroup
                    className="mb-4 w-full"
                    color="primary"
                    exclusive
                    onChange={handleModeChange}
                    sx={{
                            "& .MuiToggleButton-root": {
                                "color":       palette.overlayTextPrimary,
                                "borderColor": palette.overlayBorder,
                            },
                            "& .Mui-selected": {
                                "backgroundColor": `${ palette.selectorActiveBackgroundStrong } !important`,
                                "color":           `${ palette.selectorActiveText } !important`,
                            },
                        }}
                    value={dateMode}
                >
                        <ToggleButton
                            className="flex-1"
                            value="departure"
                        >
                            <SearchBarDepartureIcon
                                aria-label="Departure"
                                className="w-6 h-6 inline-block align-middle"
                                style={{ "color": palette.overlayTextPrimary }}
                            />
                            {" "}
                            {DEPARTURE_LABEL}
                        </ToggleButton>
                        <ToggleButton
                            className="flex-1"
                            value="arrival"
                        >
                            {ARRIVAL_ICON}
                            {" "}
                            {ARRIVAL_LABEL}
                        </ToggleButton>
                </ToggleButtonGroup>
                <IonDatetime
                    color="rose"
                    firstDayOfWeek={1}
                    locale="fr-FR"
                    min={minSelectableDate}
                    onIonChange={handleDateChange}
                    presentation={isIOS ? "date-time" : "date"}
                    value={isIOS ? iosDateTimeValue : selectedDate}
                />
                <div
                    className="flex flex-row items-center justify-between gap-3 mt-0 pt-4 border-t"
                    style={{ "borderColor": palette.overlayBorder }}
                >
                        {!isIOS && (
                            <TimePicker
                                className="w-32"
                                closeOnSelect={false}
                                onChange={handleTimePickerChange}
                                slotProps={{
                                    "textField": {
                                        "onKeyDown": handleTimeInputKeyDown,
                                        "sx":        {
                                            "& .MuiInputBase-input":              { "color": palette.overlayTextPrimary },
                                            "& .MuiOutlinedInput-notchedOutline": { "borderColor": palette.overlayBorder },
                                        },
                                    },
                                }}
                                value={selectedTime}
                            />
                        )}
                        <IonIcon
                            className="px-4 w-6 h-6 cursor-pointer transition-colors"
                            icon={refreshCircleOutline}
                            onClick={handleRefreshClick}
                            style={{ "color": palette.overlayTextSecondary }}
                        />
                        <button
                            className="px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow active:scale-95 border-none outline-none"
                            onClick={handleConfirmClick}
                            style={{
                                "backgroundColor": palette.brandPrimary,
                                "color":           palette.textOnBrandPrimary,
                            }}
                        >
                            Confirmer
                        </button>
                </div>
            </div>
        </IonModal>
    );
};

export default MuiCalendar;
